import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import {
  Text,
  Grid,
  Title,
  LoadingOverlay,
  Modal,
  Group,
  Button,
  Loader,
  Divider,
} from '@mantine/core';
import './pricing.css';
import { PricingService } from "../../services/pricingService";
import { ConfigContext } from '../../Context/configContext';
import { useTitle } from '../hooks/useTitle';
import { showNotification } from '@mantine/notifications';
import { useLocation } from "react-router-dom"; // Import useLocation to get the query parameters

export const APP_URL = "https://app.cookiex.io";

const useQuery = () => {
  return new URLSearchParams(useLocation().search);
};
// Headings
export const PRICING_HEADINGS = {
  top: "Pricing",
  title: "Choose a Plan That Works For You",
  subtitle: "14-day free trial. No fee till your trial ends. Cancel anytime.",
};

const PricingScreen = (props:any) => {
  const { config } = useContext(ConfigContext);
  const pricingService = useMemo(() => new PricingService(config!), [config]);
  const [loading, setLoading] = useState(false);
  const [subscriptionsLoading, setSubscriptionsLoading] = useState(true);
  const [subscriptionLoader, setSubscriptionLoader] = useState(false);
  const [activeTab, setActiveTab] = useState<any>('monthly');
  const [planData, setPlanData] = useState<any>(null);
  const initialRef = useRef(false);
  const query = useQuery();
  const [domainId, setDomainId] = useState<string | null>(query.get("domainId"));
  const [opened, setOpened] = useState(false);
  const [cancelOpened, setCancelOpened] = useState(false);
  const [downGradeOpened, setDownGradeOpened] = useState(false);
  const [subUrl, setSubUrl] = useState('');
  const [selectedPlanId, setSelectedPlanId] = useState<any>(null);
  const [actionType, setActionType] = useState<any>(null);
  const [subscriptionData, setSubscriptionData] = useState<any>(null); // ✅ to store current subscription

  useTitle("Pricing");

  useEffect(() => {
    if (props.currentConsentData?.domainId) {
      setDomainId(props.currentConsentData.domainId);
  
      // Update URL without reload
      const url = new URL(window.location.href);
      url.searchParams.set('domainId', props.currentConsentData.domainId);
  
      window.history.replaceState({}, '', url.toString());
    }
  }, [props.currentConsentData]);
  
  useEffect(() => {
    if (!domainId || !planData) return;
  
    const fetchSubscriptions = async () => {
      setSubscriptionsLoading(true);
      try {
        const response = await pricingService.getSubscriptions(domainId);
        const activeSubscriptions = Array.isArray(response?.data)
          ? response.data.filter((sub: any) => sub.status === 'active' || sub.status === 'activated')
          : [];
  
        if (activeSubscriptions.length > 0) {
          const paidSubscription = activeSubscriptions.find((sub: any) => sub.plan !== 'free');
          const subscriptionToUse = paidSubscription || activeSubscriptions[0];
          setSubscriptionData(subscriptionToUse);
  
          // ✅ Set activeTab based on current planId
          const currentPlanId = subscriptionToUse.planId;
          for (const plan of Object.values(planData.plans) as any) {
            if (plan.monthly?.planId === currentPlanId) {
              setActiveTab('monthly');
              break;
            }
            if (plan.yearly?.planId === currentPlanId) {
              setActiveTab('yearly');
              break;
            }
          }
        } else {
          setSubscriptionData(null);
        }
      } catch (error) {
        console.error("Error fetching subscriptions:", error);
        setSubscriptionData(null);
      } finally {
        setSubscriptionsLoading(false);
      }
    };
  
    fetchSubscriptions();
  }, [domainId, planData]);
    
  const getPlans = useCallback(async () => {
      setLoading(true);
      try {
          const response = await fetch(config!.pricingUrl);
          const data = await response.json();
          setPlanData(data);
      } catch (error) {
          console.error("Error fetching domains:", error);
          showNotification({ color: "red", message: "Failed to fetch Plans." });
      } finally {
          setLoading(false);
      }
  }, [pricingService, domainId]);

  useEffect(() => {
    if(!initialRef.current) {
      initialRef.current = true;
      getPlans();
    }
  }, [getPlans]);
  
  const onSelectPlan = useCallback(
    (planId: any, action: any, provider: any) => {
      setActionType(action);
      setSelectedPlanId(planId);
  
      if (action === "Cancel") {
        setCancelOpened(true);
        return;
      }
  
      if (action === "Downgrade") {
        setDownGradeOpened(true);
        return;
      }
  
      if (action === "Upgrade" || action === "Renew") {
        // ✅ Directly open the Razorpay URL
        const fetchPlan = async () => {
          setSubscriptionLoader(true);
          try {
            const payload = { planId, provider };
            const result = await pricingService.savePlan(payload, domainId as string);
  
            if (result.subUrl) {
              getPlans(); // optional refresh
              window.open(result.subUrl, '_blank', 'noopener,noreferrer'); // 🔗 open Razorpay URL
            } else {
              throw new Error("No subUrl returned");
            }
          } catch (error:any) {
            showNotification({
              color: "red",
              message: error.response.data,
            });
          } finally {
            setSubscriptionLoader(false);
          }
        };
  
        fetchPlan();
        return;
      }
  
      // Fallback (if needed for other actions)
      setOpened(true);
    },
    [activeTab, domainId, pricingService, getPlans]
  );
  
  const handleCopyUrl = () => {
    navigator.clipboard.writeText(subUrl);
    showNotification({ color: "green", message: "URL copied to clipboard!" });
  };

  const handleOpenLink = () => {
    if (subUrl) {
      window.open(subUrl, '_blank', 'noopener,noreferrer');
    } else {
      showNotification({ color: "red", message: "Payment link is not available." });
    }
  };

  const handleCancel = async () => {
    if(domainId) {
      try {
        await pricingService.deletePlan(domainId);
        showNotification({ color: "green", message: "Plan deleted successfully!" });
        await getPlans();
        setCancelOpened(false);
      } catch (error) {
        console.error("Error canceling subscription:", error);
      }
    }
  };

  const handleDownGrade = async () => {
    if(domainId) {
      try {
        const payload = {planId: selectedPlanId, remainingCount: activeTab === 'yearly' ? 12 : 1};
        const result = await pricingService.updatePlan(domainId, payload);
        console.log(result);
        await getPlans();
        setDownGradeOpened(false);
      } catch (error) {
        console.error("Error canceling subscription:", error);
      }
    }
  };

  const getCurrency = (price: number, currency: string = "USD") => {
    return new Intl.NumberFormat(document.documentElement.lang || window.navigator.language, {
      style: "currency",
      currency,
      minimumFractionDigits: 0, // No decimal places
      maximumFractionDigits: 0,
    }).format(price);
  };


  const isReady = planData && !subscriptionsLoading;

  useEffect(() => {
    if (!planData || !subscriptionData) return;
  
    const { plans } : any = planData;
    const currentPlanId = subscriptionData.planId;
  
    for (const plan of Object.values(plans) as any) {
      if (plan.monthly?.planId === currentPlanId) {
        setActiveTab('monthly');
        return;
      }
      if (plan.yearly?.planId === currentPlanId) {
        setActiveTab('yearly');
        return;
      }
    }
  }, [planData, subscriptionData]);
    
  return (
    <React.Fragment>
       {(loading || subscriptionsLoading) && (
          <LoadingOverlay visible={true} zIndex={1000}   overlayProps={{ radius: 'sm', blur: 2 }} loaderProps={{ color: 'green', type: 'bars' }}/>
      )}
    <>
    {isReady && (
    <div className="container">
        <div className="heading f-heading text-center">
            <Title order={4} className="top-hd">{PRICING_HEADINGS.top}</Title>
            <Title className='h2' order={2}>{PRICING_HEADINGS.title}</Title>
            <Text size='md'>{PRICING_HEADINGS.subtitle}</Text>
        </div>
        <ul className={`pricing-tabs ${activeTab === 'monthly' ? '' : 'rmv-ofr'}`}>
          <li
            className={`pricing-tab-link ${activeTab === 'monthly' ? 'current' : ''}`}
            onClick={() => setActiveTab('monthly')}
          >
          Monthly
          </li>
          <li
            className={`pricing-tab-link ${activeTab === 'yearly' ? 'current' : ''}`}
            onClick={() => setActiveTab('yearly')}
          >
            Yearly
          </li>
        </ul>

        <div id={`pricing-tab-${activeTab}`} className="pricing-tab-content current">
          {planData && planData.plans && Object.keys(planData.plans).length > 0 ? (
            <Grid className="row pricing-cards">
              {Object.entries(planData.plans).map(([planKey, plan]: any, index: number) => (
                <Grid.Col
                  key={index}
                  className={`prc-col ${planKey === "pro" ? "special" : ""}`}
                  span={{ base: 12, md: 6, lg: 3 }}
                >
                  <div className="p-card">
                    <div style={{padding:'25px'}}>
                      <Title className="h4" order={4}>
                        {planKey.charAt(0).toUpperCase() + planKey.slice(1)}
                      </Title>
                      <Text className="p" style={{marginTop:'20px'}}>
                        {planKey === "free" && "For blogs and personal websites"}
                        {planKey === "basic" && "For niche audience or startups"}
                        {planKey === "pro" && "For small to medium sized businesses"}
                        {planKey === "ultimate" && "For large businesses with high traffic"}
                      </Text>
                      <Title className="h3" order={3} style={{marginTop:'20px'}}>
                        {getCurrency(plan[activeTab].price, "INR")}{" "}
                        <span className="sm">/{activeTab}</span>
                      </Title>
                      {(() => {
                        const currentPlanId = subscriptionData?.planId;
                        const currentPrice = subscriptionData?.price || 0;
                        const thisPlanId = plan[activeTab].planId;
                        const thisPlanPrice = plan[activeTab].price ?? 0;
                        console.log('Comparing Plan IDs:', { plan,planKey, currentPlanId });

                        // 4. Free Plan - No Renew, No Upgrade/Downgrade
                        if (planKey === "free") {
                          return null; // ❌ Hide button for Free Plan
                        }

                        // 1. Current Plan - Renew
                        if (thisPlanId === currentPlanId) {
                          return (
                            <Button
                              onClick={() => onSelectPlan(thisPlanId, "Renew", planData.payment_gw)}
                              className="get-started my-btn my-btn-2"
                            >
                              Renew
                            </Button>
                          );
                        }

                        // 2. Lower Price Plan - Downgrade
                        if (thisPlanPrice < currentPrice) {
                          return (
                            <Button
                              onClick={() => onSelectPlan(thisPlanId, "Downgrade", planData.payment_gw)}
                              className="get-started my-btn my-btn-2"
                            >
                              Downgrade
                            </Button>
                          );
                        }

                        // 3. Higher Price Plan - Upgrade
                        if (thisPlanPrice > currentPrice) {
                          return (
                            <Button
                              onClick={() => onSelectPlan(thisPlanId, "Upgrade", planData.payment_gw)}
                              className="get-started my-btn my-btn-2"
                            >
                              Upgrade
                            </Button>
                          );
                        }

                        return null; // no button by default
                      })()}
                    </div>
                    <div style={{padding:'0px 25px 0px 25px'}}>
                    <Divider size="sm" />
                    </div>
                    <ul className="tab-list">
                      {planKey === "free" && (
                        <>
                          <li>5,000 pageviews/month</li>
                          <li>Basic customization</li>
                        </>
                      )}
                      {planKey === "basic" && (
                        <>
                          <li>50,000 pageviews/month</li>
                          <li>Custom branding</li>
                        </>
                      )}
                      {planKey === "pro" && (
                        <>
                          <li>2,50,000 pageviews/month</li>
                          <li>Geo-targeted cookie banner</li>
                        </>
                      )}
                      {planKey === "ultimate" && (
                        <>
                          <li>Unlimited pageviews</li>
                          <li>Geo-targeted cookie banner</li>
                        </>
                      )}
                    </ul>
                  </div>
                </Grid.Col>
              ))}
            </Grid>
          ) : (
            <Text className="no-plans">No plans available for {activeTab}.</Text>
          )}
        </div>
        <Modal withCloseButton={false} opened={opened} onClose={() => setOpened(false)} centered>
        <>
          {subscriptionLoader && (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '300px' }}>
              <Loader variant="bars" color='green' />
            </div>
            
          )}
          {!subscriptionLoader && (
            <div>
              <Title order={3} className="text-center">Complete Your Subscription</Title>
              <Text size="sm" className="text-center" mt={20} mb={20}>
                Use the link below to complete your payment:
              </Text>
              <Group justify="center" mt={20}>
                <Button variant="filled" color="green" onClick={handleOpenLink}>
                  Open Payment Link
                </Button>
                <Button variant="outline" color="blue" onClick={handleCopyUrl}>
                  Copy URL
                </Button>
              </Group>
            </div>
          )}
        </>
      </Modal>
      <Modal withCloseButton={false} opened={cancelOpened} onClose={() => setCancelOpened(false)} centered>
        <Title order={3} className="text-center">Cancel Subscription</Title>
        <Text size="sm" className="text-center" mt={20} mb={20}>
          Are you sure you want to cancel your subscription? This action cannot be undone.
        </Text>
        <Group justify="center" mt={20}>
          <Button variant="filled" color="red" onClick={handleCancel}>
            Confirm
          </Button>
          <Button variant="outline" color="blue" onClick={() => setCancelOpened(false)} >
            Cancel
          </Button>
        </Group>
      </Modal>
      <Modal withCloseButton={false} opened={downGradeOpened} onClose={() => setDownGradeOpened(false)} centered>
        <Title order={3} className="text-center"> {actionType === 'Upgrade' ? 'Upgrade' : 'Downgrade'} Subscription</Title>
        <Text size="sm" className="text-center" mt={20} mb={20}>
          Are you sure you want to {actionType === 'Upgrade' ? 'Upgrade' : 'Downgrade'} your subscription? This action cannot be undone.
        </Text>
        <Group justify="center" mt={20}>
          <Button variant="filled" color="red" onClick={handleDownGrade}>
            Confirm
          </Button>
          <Button variant="outline" color="blue" onClick={() => setDownGradeOpened(false)}>
            Cancel
          </Button>
        </Group>
      </Modal>
      </div>
    )}
    </>
    </React.Fragment>
  );
};

export { PricingScreen };
